import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Upload as UploadIcon,
  Person as PersonIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { theme } from '../styles/theme';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;
const collapsedWidth = 70;

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SidebarContainer = styled('div')(({ theme }) => ({
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  padding: theme.spacing(3),
  width: 300,
  height: '100vh',
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const SidebarHeader = styled('h1')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: theme.spacing(3),
}));

const NavigationItem = styled('div')<{ active?: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(2),
  margin: `${theme.spacing(1)} 0`,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  background: active ? theme.palette.primary.main + '10' : 'transparent',
  transition: theme.transitions.create('background'),
  '&:hover': {
    background: active ? theme.palette.primary.main + '15' : theme.palette.primary.main + '05',
  },
}));

export function Sidebar({ isOpen, setIsOpen }: SidebarProps) {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const { logout, userEmail, username, isAdmin } = useAuth();

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Propensity', icon: <InsightsIcon />, path: '/propensity' },
    ...(isAdmin ? [{ text: 'Excel Upload', icon: <UploadIcon />, path: '/excel-upload' }] : []),
    // { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const handleLogout = () => {
    logout();
  };

  // Get initials for avatar
  const getInitials = () => {
    if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    if (userEmail) {
      return userEmail.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const drawer = (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* User Profile Section - Only show when drawer is open */}
      {isOpen && (
        <Box sx={{ 
          py: 2, 
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ 
            bgcolor: 'primary.main',
            width: 40,
            height: 40
          }}>
            {getInitials()}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {username || 'User'}
            </Typography>
            {userEmail && (
              <Tooltip title={userEmail}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 150
                  }}
                >
                  {userEmail}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
      
      {isOpen && <Divider sx={{ mb: 1 }} />}

      {/* Navigation Menu */}
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        pb: 7,
      }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton 
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: isOpen ? 'initial' : 'center',
                  px: 2.5,
                  '&:hover': {
                    background: 'rgba(20, 190, 240, 0.08)',
                  },
                  '&.Mui-selected': {
                    background: 'rgba(40, 50, 140, 0.12)',
                    '&:hover': {
                      background: 'rgba(40, 50, 140, 0.16)',
                    }
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: 'primary.main',
                    minWidth: 0,
                    mr: isOpen ? 2 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isOpen && (
                  <ListItemText 
                    primary={item.text} 
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: 500,
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        zIndex: 1,
      }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                justifyContent: isOpen ? 'initial' : 'center',
                px: 2.5,
                transition: 'all 0.2s ease',
                borderRadius: '4px',
                mx: 1,
                border: theme => `1px solid ${theme.palette.error.main}`,
                '&:hover': {
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? 'rgba(255, 70, 70, 0.2)'
                    : 'rgba(255, 70, 70, 0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isOpen ? 2 : 'auto',
                  justifyContent: 'center',
                  color: theme => theme.palette.error.main,
                  transition: 'color 0.2s ease',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {isOpen && (
                <ListItemText
                  primary="Logout"
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 600,
                      color: theme => theme.palette.error.main,
                    }
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isOpen ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isOpen ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
          border: 'none',
          borderRight: 1,
          borderColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(255,255,255,0.1)' 
            : 'rgba(0,0,0,0.1)',
          height: '100%',
          position: 'relative',
        },
      }}
      open={isOpen}
    >
      {drawer}
    </Drawer>
  );
} 